<template>
  <FlashNotifications ref="flash" />

  <div class="relative min-h-[calc(100vh-56px)] overflow-hidden bg-gradient-to-b from-white via-blue-50/30 to-white dark:from-[#0d1117] dark:via-[#0d1117] dark:to-[#0d1117]">

    <!-- Ambient glow -->
    <div
      class="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-blue-400/10 dark:bg-blue-500/10 blur-3xl"
      aria-hidden="true"
    ></div>

    <!-- ═══════════════════════════════════════════════════════
         LOADING SKELETON
         Mirrors the "switch organization" grid below. Box count comes
         from the last known organization count (cached in localStorage)
         so returning visits render the right number of boxes immediately;
         first-ever visits fall back to a default count.
    ════════════════════════════════════════════════════════ -->
    <div v-if="orgsLoading" class="relative px-4 sm:px-6 py-10 sm:py-16">
      <div class="mx-auto w-full max-w-5xl animate-pulse">
        <div class="text-center mb-10">
          <div class="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded-full mx-auto mb-3"></div>
          <div class="h-7 w-72 max-w-full bg-gray-200 dark:bg-gray-700 rounded-lg mx-auto mb-3"></div>
          <div class="h-3.5 w-52 bg-gray-100 dark:bg-gray-800 rounded mx-auto"></div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="n in skeletonCount"
            :key="n"
            class="flex items-center gap-4 rounded-2xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-[#1C2128] p-4"
          >
            <div class="w-11 h-11 rounded-xl bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
            <div class="flex-1 min-w-0 space-y-2">
              <div class="h-3.5 w-2/3 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div class="h-3 w-14 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
            </div>
            <div class="w-4 h-4 bg-gray-100 dark:bg-gray-800 rounded flex-shrink-0"></div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="relative px-4 sm:px-6 py-10 sm:py-16">
      <div class="mx-auto w-full max-w-5xl">

        <!-- ═══════════════════════════════════════════════════════
             CREATE ORGANIZATION
        ════════════════════════════════════════════════════════ -->
        <template v-if="organizations.length === 0 || showForm">
          <div class="mx-auto max-w-md">

            <button
              v-if="showForm && organizations.length > 0"
              @click="showForm = false"
              type="button"
              class="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
            >
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back to organizations
            </button>

            <div class="text-center mb-8">
              <!-- Live avatar preview, updates as the name is typed -->
              <div
                class="mx-auto mb-5 w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold shadow-lg shadow-blue-900/10 dark:shadow-black/30 transition-colors duration-500 ease-out motion-reduce:transition-none"
                :class="newOrgName.trim() ? [previewColorClass, 'text-white'] : ['bg-gray-100 dark:bg-gray-800', 'text-gray-300 dark:text-gray-600']"
              >
                <span v-if="newOrgName.trim()">{{ previewInitials }}</span>
                <svg v-else class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>

              <p class="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-2">
                {{ organizations.length === 0 ? 'Get started' : 'New organization' }}
              </p>
              <h1 class="text-[28px] sm:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">Name your organization</h1>
              <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">A shared home for your team's events, bookings and members.</p>
            </div>

            <div class="bg-white dark:bg-[#1C2128] rounded-2xl shadow-xl shadow-gray-900/5 dark:shadow-black/30 border border-gray-100 dark:border-gray-700/60 p-6 sm:p-7">
              <label for="org-name" class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Organization name <span class="text-red-500">*</span>
              </label>
              <input
                id="org-name"
                ref="nameInput"
                v-model="newOrgName"
                type="text"
                maxlength="100"
                placeholder="Acme Sales Team"
                @keydown.enter="createOrganization"
                class="w-full px-4 py-3 text-base border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 transition-colors"
              >

              <div class="mt-2 flex items-start justify-between gap-4">
                <p v-if="error" class="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                  <svg class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                  </svg>
                  {{ error }}
                </p>
                <p v-else class="text-xs text-gray-400 dark:text-gray-500">This name appears to your team and on your public profile.</p>
                <span class="text-xs text-gray-300 dark:text-gray-600 tabular-nums flex-shrink-0">{{ newOrgName.length }}/100</span>
              </div>

              <button
                @click="createOrganization"
                :disabled="!newOrgName.trim() || loading"
                type="button"
                class="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-blue-300 dark:disabled:bg-blue-900/40 disabled:cursor-not-allowed rounded-xl shadow-sm shadow-blue-600/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#1C2128]"
              >
                <svg v-if="loading" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {{ loading ? 'Creating organization...' : 'Create organization' }}
              </button>
            </div>

            <p class="mt-5 text-center text-xs text-gray-400 dark:text-gray-500">You can invite teammates and adjust settings right after.</p>
          </div>
        </template>

        <!-- ═══════════════════════════════════════════════════════
             SWITCH ORGANIZATION
        ════════════════════════════════════════════════════════ -->
        <template v-else>
          <div class="text-center mb-10">
            <p class="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-2">Organizations</p>
            <h1 class="text-[28px] sm:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
              Welcome back{{ firstName ? `, ${firstName}` : '' }}
            </h1>
            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Pick an organization to continue.</p>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <a
              v-for="org in organizations"
              :key="org.id"
              :href="`/organizations/${org.url}/dashboard`"
              class="group flex items-center gap-4 rounded-2xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-[#1C2128] p-4 shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-blue-200 dark:hover:border-blue-800 transition-all duration-200 motion-reduce:transition-none motion-reduce:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <div v-if="org.profile_picture_url" class="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 border border-gray-100 dark:border-gray-700">
                <img :src="org.profile_picture_url" :alt="org.name" class="w-full h-full object-cover">
              </div>
              <div v-else :class="org.avatar_color" class="w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center text-white text-sm font-bold">
                {{ org.avatar_name }}
              </div>

              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{{ org.name }}</p>
                <span :class="roleBadgeClass(org.uo_role)" class="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border leading-none">
                  <span class="w-1.5 h-1.5 rounded-full" :class="roleDotClass(org.uo_role)"></span>
                  {{ formatRole(org.uo_role) }}
                </span>
              </div>

              <svg class="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>

            <!-- New organization tile -->
            <button
              type="button"
              @click="openForm"
              class="group flex items-center gap-4 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-4 text-left hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <div class="w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-400 group-hover:text-blue-500 group-hover:border-blue-300 dark:group-hover:text-blue-400 dark:group-hover:border-blue-700 transition-colors">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-gray-700 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">New organization</p>
                <p class="text-xs text-gray-400 dark:text-gray-500">Start a new organization</p>
              </div>
            </button>
          </div>
        </template>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRequest } from 'composables/useRequest'
import { useGlobalStore } from 'stores/global.js'
import { getCachedOrganizationsCount, setCachedOrganizationsCount } from 'composables/useOrganizationsCountCache'
import FlashNotifications from 'controllers/vue_components/flash_toast'

const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const store = useGlobalStore()

const organizations = ref([])
const orgsLoading = ref(true)
// Read synchronously at setup time so the very first render already shows
// the right number of skeleton boxes (from a prior visit), no flash of a
// wrong count once the component mounts.
const skeletonCount = ref(getCachedOrganizationsCount())
const newOrgName = ref('')
const showForm = ref(false)
const loading = ref(false)
const error = ref(null)
const nameInput = ref(null)

// Same palette the backend assigns to real organizations (see app/assets/tailwind/application.css)
const AVATAR_COLORS = ['peacock', 'sage', 'tangerine', 'grape', 'flamingo', 'blueberry', 'basil', 'tomato', 'lavender', 'graphite', 'banana']

onMounted(async () => {
  const res = await axiosRequest('GET', '/api/v1/organizations', {}, false)
  if (!res.js_error && res.response.success) {
    organizations.value = res.response.data
    setCachedOrganizationsCount(organizations.value.length)
  }
  orgsLoading.value = false
  if (organizations.value.length === 0) {
    await nextTick()
    nameInput.value?.focus()
  }
})

function openForm() {
  showForm.value = true
  nextTick(() => nameInput.value?.focus())
}

const firstName = computed(() => store.user.display_name?.trim().split(/\s+/)[0] || '')

function avatarChars(name) {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const result = words.map((w) => w[0]).slice(0, 2).join('').toUpperCase()
  return result.length === 1 ? name.trim().slice(0, 2).toUpperCase() : result
}

function hashToIndex(str, length) {
  let hash = 0
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) % length
  return hash
}

const previewInitials = computed(() => (newOrgName.value.trim() ? avatarChars(newOrgName.value) : ''))
const previewColorClass = computed(() => AVATAR_COLORS[hashToIndex(newOrgName.value.trim().toLowerCase(), AVATAR_COLORS.length)])

async function createOrganization() {
  if (!newOrgName.value.trim() || loading.value) return

  loading.value = true
  error.value = null

  const res = await axiosRequest('POST', '/api/v1/organizations', { name: newOrgName.value.trim() }, false)

  loading.value = false

  if (!res.js_error && res.response.success) {
    window.location.href = `/organizations/${res.response.data.url}/profile`
  } else {
    error.value = res.response?.error || res.response?.message || 'Something went wrong. Please try again.'
  }
}

function formatRole(role) {
  if (!role) return 'Member'
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
}

function roleBadgeClass(role) {
  const r = (role || '').toLowerCase()
  if (r === 'owner') return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800'
  if (r === 'admin') return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800'
  return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800'
}

function roleDotClass(role) {
  const r = (role || '').toLowerCase()
  if (r === 'owner') return 'bg-emerald-500 dark:bg-emerald-400'
  if (r === 'admin') return 'bg-amber-500 dark:bg-amber-400'
  return 'bg-blue-500 dark:bg-blue-400'
}
</script>
